import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Pozycjonowanie POPOVERA renderowanego w portalu (kalendarz, menu statusu, lista wyboru)
 * przy jego polu/przycisku.
 *
 * Dlaczego hook, a nie „licznik w każdym komponencie":
 *  1. Popover w portalu ma `position: fixed`, więc trzeba mu PODAĆ współrzędne. Gdy liczy je
 *     `useEffect`, przeglądarka zdąży namalować klatkę ze współrzędnymi startowymi (0,0) —
 *     stąd „kalendarz otwiera się w lewym górnym rogu ekranu". Tu liczy to `useLayoutEffect`,
 *     czyli PRZED malowaniem, a dopóki pomiaru nie ma, popover jest `visibility: hidden`
 *     (jest w DOM, żeby dało się go zmierzyć, ale nie mignie w rogu).
 *  2. Rozmiar popovera jest MIERZONY, a nie wpisany na sztywno — inaczej odbicie w górę
 *     i docisk do krawędzi okna liczą się dla nieprawdziwej szerokości.
 *  3. Pomiar odświeża się przy przewijaniu, zmianie rozmiaru okna ORAZ zmianie rozmiaru
 *     samego popovera (miesiąc z 6 wierszami, rozwinięta siatka godzin).
 *
 * @param {boolean} open        czy popover jest otwarty (zamknięty = brak nasłuchów)
 * @param {object}  anchorRef   ref pola/przycisku, przy którym ma stanąć popover
 * @param {object}  popoverRef  ref elementu popovera (musi być w DOM, gdy `open`)
 * @param {object}  opts        { align: "left"|"right", gap, minHeight, fit, sufitUlamekOkna }
 *        `fit: true`  — popover dostaje `maxHeight` z wolnej przestrzeni i własny scroll
 *                       (kalendarze: wolimy niższy kalendarz niż kalendarz poza ekranem).
 *        `fit: false` — wysokość zostaje z CSS; hook tylko ustawia go we właściwym miejscu.
 *        `sufitUlamekOkna` — [2026-09-15] DODATKOWY sufit jako ułamek wysokości okna
 *                       (0.75 = „trzy czwarte okna przeglądarki"). Działa razem z `fit`:
 *                       ostateczne `maxHeight` to MNIEJSZA z dwóch liczb — wolne miejsce przy
 *                       polu i ten sufit. ⚠ Musi być liczony TUTAJ, a nie dokładany na zewnątrz
 *                       do zwróconego `style`: przy odbiciu w górę `top` wylicza się z wysokości
 *                       popovera, więc sufit nałożony po fakcie zostawiłby lukę między dołem
 *                       listy a polem (lista wisiałaby wyżej, niż jest wysoka).
 * @returns {{ style: object, ready: boolean, recalc: function }}
 */
export default function usePopoverPosition(open, anchorRef, popoverRef, opts = {}) {
    const { align = "left", gap = 4, minHeight = 180, fit = false, sufitUlamekOkna = 0 } = opts;
    const [pos, setPos] = useState(null);
    const frameRef = useRef(0);

    // Margines od krawędzi okna i odstęp od pola to GEOMETRIA OKNA (piksele ekranu),
    // a nie rozmiar tekstu — dlatego zostają liczbami, nie tokenami rem.
    const MARGIN = 8;

    const compute = useCallback(() => {
        const anchor = anchorRef.current;
        const pop = popoverRef.current;
        if (!anchor || !pop) return;

        const a = anchor.getBoundingClientRect();
        const vw = window.innerWidth || document.documentElement.clientWidth || 0;
        const vh = window.innerHeight || document.documentElement.clientHeight || 0;

        /* Przy `fit` bierzemy wysokość TREŚCI (`scrollHeight`) — ona nie zmienia się po
           nałożeniu `maxHeight`, więc decyzja „w dół czy w górę" jest stabilna i popover
           nie migocze między stronami. Bez `fit` wysokość jest już ustalona przez CSS. */
        const needed = Math.ceil(fit ? pop.scrollHeight : pop.offsetHeight) + 2;
        const spaceBelow = vh - a.bottom - gap - MARGIN;
        const spaceAbove = a.top - gap - MARGIN;

        const above = spaceBelow < needed && spaceAbove > spaceBelow;
        const room = Math.round(Math.max(0, above ? spaceAbove : spaceBelow));
        let maxHeight = Math.max(minHeight, room);
        // Sufit „ułamek okna" wchodzi PRZED wyliczeniem `height`, żeby odbicie w górę liczyło
        // się z tej samej wysokości, którą popover naprawdę będzie miał.
        if (sufitUlamekOkna > 0 && vh > 0) maxHeight = Math.min(maxHeight, Math.round(sufitUlamekOkna * vh));
        const height = fit ? Math.min(needed, maxHeight) : needed;

        let top = above ? a.top - gap - height : a.bottom + gap;
        top = Math.min(Math.max(MARGIN, top), Math.max(MARGIN, vh - height - MARGIN));

        const width = Math.ceil(pop.offsetWidth) || 0;
        let left = align === "right" ? a.right - width : a.left;
        if (left + width > vw - MARGIN) left = vw - width - MARGIN;
        if (left < MARGIN) left = MARGIN;
        left = Math.round(left);
        top = Math.round(top);

        setPos((prev) =>
            prev && prev.top === top && prev.left === left && prev.maxHeight === maxHeight
                ? prev
                : { top, left, maxHeight }
        );
    }, [anchorRef, popoverRef, align, gap, minHeight, fit, sufitUlamekOkna]);

    /* Przewijanie potrafi sypnąć setkami zdarzeń na sekundę — przeliczamy raz na klatkę. */
    const recalc = useCallback(() => {
        if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
            compute();
            return;
        }
        if (frameRef.current) return;
        frameRef.current = window.requestAnimationFrame(() => {
            frameRef.current = 0;
            compute();
        });
    }, [compute]);

    useLayoutEffect(() => {
        if (!open) {
            setPos(null);
            return undefined;
        }
        compute();
        window.addEventListener("resize", recalc);
        // `true` = faza przechwytywania: łapiemy też przewijanie WEWNĘTRZNYCH kontenerów
        // (treść strony, ciało okna modal), nie tylko okna przeglądarki.
        window.addEventListener("scroll", recalc, true);

        let ro = null;
        if (typeof ResizeObserver === "function") {
            ro = new ResizeObserver(recalc);
            if (popoverRef.current) ro.observe(popoverRef.current);
            if (anchorRef.current) ro.observe(anchorRef.current);
        }

        return () => {
            window.removeEventListener("resize", recalc);
            window.removeEventListener("scroll", recalc, true);
            if (ro) ro.disconnect();
        };
    }, [open, compute, recalc, anchorRef, popoverRef]);

    useEffect(() => () => {
        if (frameRef.current && typeof window !== "undefined") window.cancelAnimationFrame(frameRef.current);
    }, []);

    const style = {
        position: "fixed",
        top: pos ? pos.top : 0,
        left: pos ? pos.left : 0,
        visibility: pos ? "visible" : "hidden",
    };
    if (fit && pos) {
        style.maxHeight = pos.maxHeight;
        style.overflowY = "auto";
    }

    return { style, ready: pos !== null, recalc };
}
